/**
 * Phase 0 · Infra-1 · P0.CLI.6 + P0.CLI.7
 * Phase 1 · P1.UI.1 + P1.UI.2 — Figma rebuild
 *
 * Pre-call rationale + permission request. Handles both the "not yet asked"
 * and "denied / blocked" states. On denial, offers a link to system settings.
 *
 * Visual rebuild (April 2026): matches Figma nodes 6684:19032 (default) and
 * 6684:19076 (unallowed-before). Both states share the same chrome — only the
 * primary button label and onPress flip. All side-effect logic (auto-skip on
 * granted, handleAllow, openSettings, audio-only fallback) is preserved
 * verbatim from the previous implementation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Platform,
  Linking,
  Alert,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useMicPermission } from '@/hooks/useMicPermission';
import { MicGlyph, RecordVideoGlyph } from '@/components/video/lobby/LobbyControlPill';

const PHOTO_TOP = require('@/assets/video-call/permission-photo-top.png');
const PHOTO_BOTTOM = require('@/assets/video-call/permission-photo-bottom.png');

export default function VideoCallPermissionsScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    sessionType?: string;
    sessionId?: string;
    mode?: 'audio' | 'video';
    // Pre-minted by perfil's Join button so the lobby can reuse it via
    // requestVideoToken({linkToken}) without a second round-trip.
    linkToken?: string;
    channel?: string;
  }>();
  const mic = useMicPermission();
  const [requesting, setRequesting] = useState(false);

  const blocked = mic.status === 'blocked';
  const wantsVideo = (params.mode ?? 'video') === 'video';

  // If both mic + camera are already granted on entry (e.g. user came back to
  // start a second call), skip this rationale screen entirely.
  const autoSkippedRef = useRef(false);
  useEffect(() => {
    if (autoSkippedRef.current) return;
    if (mic.loading) return;
    if (mic.status !== 'granted') return;
    autoSkippedRef.current = true;
    router.replace({
      pathname: '/video-call/lobby',
      params: {
        sessionType: String(params.sessionType ?? 'appointment'),
        sessionId: String(params.sessionId ?? ''),
        mode: wantsVideo ? 'video' : 'audio',
        ...(params.linkToken ? { linkToken: String(params.linkToken) } : {}),
        ...(params.channel ? { channel: String(params.channel) } : {}),
      },
    });
  }, [
    mic.loading,
    mic.status,
    params.sessionId,
    params.sessionType,
    params.linkToken,
    params.channel,
    router,
    wantsVideo,
  ]);

  const handleAllow = useCallback(async () => {
    setRequesting(true);
    try {
      const next = await mic.request();
      // Both platforms now return authoritative state — only advance when the
      // user actually granted both mic and camera. Anything else stays on this
      // screen (state will re-render into the "denied" or "blocked" branch).
      const canProceed = next === 'granted';

      if (canProceed) {
        router.replace({
          pathname: '/video-call/lobby',
          params: {
            sessionType: String(params.sessionType ?? 'appointment'),
            sessionId: String(params.sessionId ?? ''),
            mode: wantsVideo ? 'video' : 'audio',
            ...(params.linkToken ? { linkToken: String(params.linkToken) } : {}),
            ...(params.channel ? { channel: String(params.channel) } : {}),
          },
        });
      }
      // If Android returned 'denied' / 'blocked', state change re-renders
      // the copy into the "bloqueado → abrir configurações" branch.
    } catch (err: any) {
      Alert.alert('Erro', err?.message || 'Não foi possível solicitar permissão.');
    } finally {
      setRequesting(false);
    }
  }, [mic, params, router, wantsVideo]);

  const openSettings = useCallback(async () => {
    try {
      await Linking.openSettings();
    } catch {
      Alert.alert(
        'Abra as configurações',
        'Vá em Ajustes → Padrinho → ative Microfone' +
          (wantsVideo ? ' e Câmera.' : '.')
      );
    }
  }, [wantsVideo]);

  const copy = useMemo(() => {
    if (blocked) {
      return {
        title: wantsVideo
          ? 'Habilite seu microfone\ne sua câmera'
          : 'Habilite seu microfone',
        body: wantsVideo
          ? 'Para sua videochamada, é necessário permitir o acesso ao seu microfone e câmera. Nós não gravamos sua conversa!'
          : 'Para sua chamada de voz, é necessário permitir o acesso ao seu microfone. Nós não gravamos sua conversa!',
        cta: 'Abrir minhas configurações',
        onPress: openSettings,
      };
    }
    return {
      title: wantsVideo
        ? 'Habilite seu microfone\ne sua câmera'
        : 'Habilite seu microfone',
      body: wantsVideo
        ? 'Para sua videochamada, é necessário permitir o acesso ao seu microfone e câmera. Nós não gravamos sua conversa!'
        : 'Para sua chamada de voz, é necessário permitir o acesso ao seu microfone. Nós não gravamos sua conversa!',
      cta: 'Permitir acesso',
      onPress: handleAllow,
    };
  }, [blocked, handleAllow, openSettings, wantsVideo]);

  return (
    <SafeAreaView style={styles.safe}>
      {/* Header — generic "Agendamento" label per privacy contract; no sponsor name. */}
      <View style={styles.header}>
        <View style={styles.headerSpacer} />
        <Text style={styles.headerTitle}>Agendamento</Text>
        <TouchableOpacity
          style={styles.headerClose}
          onPress={() => router.back()}
          hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
          accessibilityLabel="Fechar"
        >
          <Ionicons name="close" size={24} color="#151515" />
        </TouchableOpacity>
      </View>

      {/* Photo stack — decorative preview of what a call looks like. */}
      <View style={styles.photoStack}>
        <View style={styles.photoCard}>
          <Image source={PHOTO_TOP} style={styles.photo} resizeMode="cover" />
          {/* Overlay control pills (mic + camera) — purely decorative on this screen. */}
          <View style={styles.pillRow}>
            {/* Decorative pills mirror the Lobby controls (Figma node 6686:20178)
                — same Mic + RecordVideo glyphs, same soundwave bar heights
                (10/6/10/14/6), same blue-horizon record dot. */}
            <View style={styles.pill}>
              <MicGlyph color="#151515" />
              <View style={styles.waveform}>
                {[10, 6, 10, 14, 6].map((h, i) => (
                  <View key={i} style={[styles.wave, { height: h }]} />
                ))}
              </View>
            </View>
            <View style={styles.pill}>
              <RecordVideoGlyph color="#151515" size={24} />
              <View style={styles.recordDot} />
            </View>
          </View>
        </View>
        <View style={[styles.photoCard, { marginTop: 16 }]}>
          <Image source={PHOTO_BOTTOM} style={styles.photo} resizeMode="cover" />
        </View>
      </View>

      {/* Gradient fade from the photos into the cream background, then copy. */}
      <View style={styles.bottomFill} pointerEvents="box-none">
        <LinearGradient
          colors={['rgba(249,248,243,0)', '#F9F8F3']}
          locations={[0, 0.28]}
          style={styles.gradient}
          pointerEvents="none"
        />
        <View style={styles.bodyText}>
          <Text style={styles.title}>{copy.title}</Text>
          <Text style={styles.body}>{copy.body}</Text>
        </View>

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.primaryButton, requesting && styles.primaryButtonDisabled]}
            onPress={copy.onPress}
            disabled={requesting}
            activeOpacity={0.85}
            accessibilityRole="button"
          >
            {requesting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>{copy.cta}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => router.back()}
            hitSlop={{ top: 12, bottom: 12, left: 24, right: 24 }}
            accessibilityRole="button"
          >
            <Text style={styles.secondaryLink}>Agora não</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

const CREAM = '#F9F8F3';
const INK = '#151515';
const BLUE_DEEP = '#015480';
const PHOTO_BG = '#A19F97'; // bege/dawn — matches the Figma photo card placeholder bg

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: CREAM,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'android' ? 16 : 8,
    paddingBottom: 8,
    height: 48,
  },
  headerSpacer: {
    width: 40,
  },
  headerTitle: {
    color: INK,
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 17,
  },
  headerClose: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoStack: {
    paddingHorizontal: 24,
    marginTop: 8,
  },
  photoCard: {
    width: '100%',
    height: 224,
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: PHOTO_BG,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  pillRow: {
    position: 'absolute',
    left: 12,
    bottom: 12,
    flexDirection: 'row',
    gap: 4,
  },
  pill: {
    height: 48,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  waveform: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 16,
  },
  wave: {
    width: 2.5,
    backgroundColor: '#669AB7',
    borderRadius: 2,
  },
  recordDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#669AB7',
  },
  bottomFill: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 24,
    justifyContent: 'flex-end',
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -120,
    height: 200,
  },
  bodyText: {
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 8,
    marginBottom: 24,
  },
  title: {
    color: BLUE_DEEP,
    fontFamily: 'InstrumentSerif_400Regular',
    fontSize: 32,
    lineHeight: 38,
    textAlign: 'center',
  },
  body: {
    color: INK,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  footer: {
    gap: 12,
    alignItems: 'center',
  },
  primaryButton: {
    width: '100%',
    height: 56,
    borderRadius: 16,
    backgroundColor: INK,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
  },
  secondaryLink: {
    color: INK,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    paddingVertical: 8,
  },
});
